import create from "zustand";
import { ArtistId } from "../../models/Artist";
import {
  SongId,
  SongRef,
  FollowRef,
  FavoriteAlbumsRef,
} from "../../models/Song";
import { OwnerId } from "../../models/User";
import { FavoriteService } from "../../services/FavoriteService";
import { toTimestamp, uniq } from "../../util/sugar";
import { AlbumId } from "../../models/Albums";

export type FavoriteState = {
  favorites: SongRef[];
  followList: FollowRef[];
  favoriteAlbums: FavoriteAlbumsRef[];
  addFavorite: (ownerId: OwnerId, songId: SongId) => void;
  removeFavorite: (ownerId: OwnerId, songId: SongId) => void;
  isFavorite: (ownerId: OwnerId, songId: SongId) => boolean;
  addFavoriteAlbum: (ownerId: OwnerId, songId: AlbumId) => void;
  removeFavoriteAlbum: (ownerId: OwnerId, songId: AlbumId) => void;
  isFavoriteAlbum: (ownerId: OwnerId, songId: AlbumId) => boolean;
  follow: (ownerId: OwnerId, artistId: ArtistId) => void;
  unfollow: (ownerId: OwnerId, artistId: ArtistId) => void;
  isFollowing: (artistId: ArtistId) => boolean;
  initializeFavorites: (ownerId: OwnerId) => void;
  isLoading: boolean;
};

export const useFavorites = create<FavoriteState>((set, store) => ({
  favorites: [],
  followList: [],
  favoriteAlbums: [],
  isLoading: false,
  initializeFavorites: async (ownerId) => {
    set({ isLoading: true });

    try {
      const { follow } = await FavoriteService.fetchArtistFollowList(ownerId);
      if (follow != null) set({ followList: follow });
    } catch (_) {}

    try {
      const { favoriteAlbums } = await FavoriteService.fetchArtistFavoriteAlbumsList(ownerId);
      if (favoriteAlbums != null) set({ favoriteAlbums });
    } catch (_) {}

    try {
      const { favorites } = await FavoriteService.fetchArtistFavoriteList(ownerId);
      set({ favorites: favorites ? [...favorites].reverse() : [], isLoading: false });
    } catch (_) {
      set({ favorites: [], isLoading: false });
    }
  },
  isFavorite: (ownerId, songId) => {
    const favorites = store().favorites;
    return favorites.map((x) => x.id).includes(songId);
  },
  addFavorite: (ownerId, songId) => {
    const favorite: SongRef = {
      id: songId,
      updatedAt: toTimestamp(Date.now()),
    };
    set({ favorites: uniq([favorite, ...store().favorites]) as SongRef[] });
    FavoriteService.AddFavorite(ownerId, favorite);
  },
  removeFavorite: (ownerId, songId) => {
    const song = store().favorites.find((s) => s.id === songId);
    if (!song) return;
    set({ favorites: store().favorites.filter((f) => f.id !== songId) });
    FavoriteService.RemoveFavorite(ownerId, song);
  },
  isFavoriteAlbum: (ownerId, albumId) => {
    const favorites = store().favoriteAlbums;
    return favorites.map((x) => x.id).includes(albumId);
  },
  addFavoriteAlbum: (ownerId, albumId) => {
    const favoriteAlbum: SongRef = {
      id: albumId,
      updatedAt: toTimestamp(new Date().getSeconds()),
    };
    const favoriteAlbumsList = store().favoriteAlbums.map(
      ({ id, updatedAt }) => ({
        id,
        updatedAt,
      })
    );
    const favoriteAlbums = uniq([
      ...favoriteAlbumsList!,
      favoriteAlbum,
    ]) as FavoriteAlbumsRef[];
    FavoriteService.changeFavoriteAlbums({ id: ownerId, favoriteAlbums });
    set({ favoriteAlbums });
  },
  removeFavoriteAlbum: (ownerId, albumId) => {
    const favoriteAlbums = store().favoriteAlbums.filter(
      (albumRef) => albumRef.id !== albumId
    );
    FavoriteService.changeFavoriteAlbums({ id: ownerId, favoriteAlbums });
    set({ favoriteAlbums });
  },
  isFollowing: (artistId) => {
    const followList = store().followList;
    return followList.map((x) => x.id).includes(artistId);
  },
  follow: (ownerId, artistId) => {
    const follow: SongRef = {
      id: artistId,
      updatedAt: toTimestamp(new Date().getSeconds()),
    };
    const followList = uniq([...store().followList!, follow]) as FollowRef[];
    FavoriteService.changeFollow({ id: ownerId, follow: followList });
    set({ followList });
  },
  unfollow: (ownerId, artistId) => {
    const followList = store().followList.filter(
      (followRef) => followRef.id !== artistId
    );
    FavoriteService.changeFollow({ id: ownerId, follow: followList });
    set({ followList });
  },
}));
